import React, { useEffect, useState } from "react";

//UI Material
import {
  Box,
  CircularProgress,
  Grid,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";

//Local components
import SpecificMonthlyProduction from "../Components/reports-page/SpecificMonthlyProduction";
import AllProductsSummary from "../Components/reports-page/AllProductsSummary";
import AllStationsSummary from "../Components/reports-page/AllStationsSummary";
import AllAccesoriesSummary from "../Components/reports-page/AllAccesoriesSummary";
import ProductCard from "../Components/reports-page/ProductCard";
import SalesCard from "../Components/reports-page/SalesCard";
import TotalMonthlyProduction from "../Components/reports-page/TotalMonthlyProduction";
import TotalMonthlySales from "../Components/reports-page/TotalMonthlySales";
import Selector from "../Components/basics/Selector";
import ProductionSummaryInfo from "../Components/reports-page/ProductionSummaryInfo";

//Helpers
import {
  typographyForCustomizedFontSize,
  typographyForText,
} from "../Helpers/typography";
import { evstations } from "../Helpers/productSelector";

//Material Icons
import {
  AttachMoney,
  EvStation,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { fetchWithTokenJSONPost } from "../Helpers/network";
import DataLoader from "../Components/basics/DataLoader";

//Years dictionary
const years = ["2023", "2024", "2025", "2026", "2027"];

const topSecretUsers = [
  "Fernando Matamoros",
  "Daniel Castillo",
  "Stephannie Friderici",
  "Francisco Acuña",
];

export default function Reports() {
  // ==========> Component States <==========
  //User data from localStorage
  const userData = JSON.parse(localStorage.getItem("userData"));
  //State for storing orders data
  const [ordersList, setOrdersList] = useState([]);
  //State for the selected year
  const [year, setYear] = useState(2);
  //State for the product type
  const [type, setType] = useState(0);
  const [viewLessSalesInfo, setViewLessSalesInfo] = useState(false);
  //3
  const [viewLessProductsInfo, setViewLessProductsInfo] = useState(false);
  //State for setting loader for orders data
  const [isLoading, setIsLoading] = useState(true);
  //State for setting error when could not get orders data
  const [dataError, setDataError] = useState(false);

  // ==========>Component UseEffects<==========
  //The useEffect checks for token validation
  useEffect(() => {
    getOrdersList();
    const interval = setInterval(() => {
      getOrdersList();
    }, 300000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  // ==========> Component Functions <==========
  //Function to get orders list
  const getOrdersList = async () => {
    try {
      const requestResponse = await fetchWithTokenJSONPost(
        `/orders/orders-list`,
        { howMany: 5000 }
      );
      const jsonResponse = await requestResponse.json();
      if (requestResponse.status === 200) {
        setOrdersList(jsonResponse.data);
      } else {
        if (requestResponse.status === 401) {
          window.location.href = "/";
        }
      }
    } catch (error) {
      setDataError(true);
    }
    setIsLoading(false);
  };

  //Function to remove canceled orders from reports
  const removeCanceledOrders = () => {
    return ordersList.filter(
      (order) =>
        !order.canceledBy &&
        order.finishedAt &&
        new Date(order.finishedAt).getFullYear() == years[year]
    );
  };

  const notCanceledOrders = removeCanceledOrders();

  //Function to get finished products of the selected year
  const getProducedQuantitiesOfTheYear = (order, array, index) => {
    for (const product of order.productsStatus) {
      if (product.status === "FINISHED") {
        if (new Date(product.finishedAt).getFullYear() == years[year]) {
          if (order.productName === "Estación Tipo Wallbox doble") {
            array[index].total += 2;
          } else {
            array[index].total += 1;
          }

          if (
            new Date(product.finishedAt).getMonth() === new Date().getMonth()
          ) {
            if (order.productName === "Estación Tipo Wallbox doble") {
              array[index].month += 2;
            } else {
              array[index].month += 1;
            }
          }
        }
      }
    }
  };

  //Function to get selected year produced quantity MONTH/ALL
  const getSelectedYearProducts = (orders) => {
    let tempProduced = [
      { month: 0, total: 0 }, //All
      { month: 0, total: 0 }, //Stations
      { month: 0, total: 0 }, //Accesories
    ];

    for (const order of orders) {
      //For all
      getProducedQuantitiesOfTheYear(order, tempProduced, 0);

      //If accesories
      if (!evstations.includes(order.productName)) {
        getProducedQuantitiesOfTheYear(order, tempProduced, 2);
      }
      //If stations

      if (evstations.includes(order.productName)) {
        getProducedQuantitiesOfTheYear(order, tempProduced, 1);
      }
    }

    return tempProduced;
  };

  //Function to set tab value
  const handleChange = (_event, newValue) => {
    setType(newValue);
  };
  const changeTab = handleChange;

  //Function for giving an index to each tab
  function a11yProps(index) {
    return {
      id: `products-tabs-${index}`,
      "aria-controls": `products-tabpanel-${index}`,
    };
  }

  //Function to generate products tabs content
  const productsTabsPlaceholder = () => {
    switch (type) {
      case 0:
        return (
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Estación Tipo Wallbox"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Estación Pública"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Estación Portátil"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Estación Portátil Plus"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Estación Tipo Wallbox con pedestal"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Estación Tipo Wallbox Plus"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Estación Tipo Wallbox con método de pago"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Estación Tipo Wallbox doble"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Cargador Portátil Dual (Genérico)"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
          </Grid>
        );
      case 1:
        return (
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Adaptador Tipo 1 a Tipo 2"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Adaptador CCS1 a CCS2"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Adaptador Tipo 2 a Tipo 1"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Adaptador Tipo 1 a GB/T"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Simulador de Estación"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Extensión VTOL"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Adaptador ChaDeMO a GB/T"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Probador de Estaciones"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Convertidor VTOL"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Kit de pared"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
          </Grid>
        );
      case 2:
        return (
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              <ProductCard
                product={"Otros productos"}
                data={notCanceledOrders}
                year={years[year]}
              />
            </Grid>
          </Grid>
        );
      default:
        return (
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6} md={4} xl={3}>
              {typographyForText("No tienes productos seleccionados.")}
            </Grid>
          </Grid>
        );
    }
  };

  if (isLoading) {
    return <DataLoader />;
  }

  // ==========> Component <==========
  return (
    <Grid container spacing={1} justifyContent="center">
      <Grid item xs={12}>
        <Typography variant="h5" fontWeight="bold">
          Reportes de Producción
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Stack spacing={1} textAlign="left">
          <Typography variant="body2">
            Seleccione un año para visualizar el reporte de producción
          </Typography>
          <Selector
            value={year}
            handleChange={(e) => {
              setYear(e.target.value);
            }}
            content={years}
          />
        </Stack>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <ProductionSummaryInfo data={notCanceledOrders} />
          </Grid>

          <Grid item xs={12} sm={12} md={4}>
            <AllProductsSummary
              data={getSelectedYearProducts(notCanceledOrders)[0]}
              orders={notCanceledOrders}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <AllStationsSummary
              data={getSelectedYearProducts(notCanceledOrders)[1]}
              orders={notCanceledOrders.filter((order) =>
                evstations.includes(order.productName)
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <AllAccesoriesSummary
              data={getSelectedYearProducts(notCanceledOrders)[2]}
              orders={notCanceledOrders.filter(
                (order) => !evstations.includes(order.productName)
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TotalMonthlyProduction
              data={notCanceledOrders}
              year={years[year]}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SpecificMonthlyProduction
              data={notCanceledOrders}
              year={years[year]}
            />
          </Grid>
        </Grid>
      </Grid>

      {/**INFORMACION DE VENTAS */}
      {topSecretUsers.includes(userData.user) && (
        <Grid item xs={12} align="left">
          <Stack direction="row" alignItems="center" spacing={1}>
            <AttachMoney sx={{ color: !viewLessSalesInfo && "#00bccd" }} />
            {typographyForCustomizedFontSize(
              "Información de Ventas",
              "11px",
              !viewLessSalesInfo && "#00bccd",
              "bold"
            )}
            <IconButton
              onClick={() => {
                setViewLessSalesInfo(!viewLessSalesInfo);
              }}
            >
              {viewLessSalesInfo ? (
                <ExpandMore />
              ) : (
                <ExpandLess color="secondary" />
              )}
            </IconButton>
          </Stack>
        </Grid>
      )}
      {topSecretUsers.includes(userData.user) && !viewLessSalesInfo && (
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={4} md={3}>
              <SalesCard data={notCanceledOrders} year={years[year]} />
            </Grid>
            <Grid item xs={12} sm={8} md={9}>
              <TotalMonthlySales data={notCanceledOrders} year={years[year]} />
            </Grid>
          </Grid>
        </Grid>
      )}

      {/**INFORMACION SOBRE LOS PRODUCTOS*/}
      <Grid item xs={12} align="left">
        <Stack direction="row" alignItems="center" spacing={1}>
          <EvStation sx={{ color: !viewLessProductsInfo && "#00bccd" }} />
          {typographyForCustomizedFontSize(
            "Información de los Productos",
            "11px",
            !viewLessProductsInfo && "#00bccd",
            "bold"
          )}
          <IconButton
            onClick={() => {
              setViewLessProductsInfo(!viewLessProductsInfo);
            }}
          >
            {viewLessProductsInfo ? (
              <ExpandMore />
            ) : (
              <ExpandLess color="secondary" />
            )}
          </IconButton>
        </Stack>
      </Grid>
      {!viewLessProductsInfo && (
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Box>
                <Tabs
                  centered
                  indicatorColor="secondary"
                  textColor="secondary"
                  value={type}
                  onChange={changeTab}
                  aria-label="products-tabs"
                >
                  <Tab
                    label="Estaciones"
                    sx={{ fontSize: "11px" }}
                    {...a11yProps(0)}
                  />
                  <Tab
                    label="Accesorios"
                    sx={{ fontSize: "11px" }}
                    {...a11yProps(1)}
                  />
                  <Tab
                    label="Otros"
                    sx={{ fontSize: "11px" }}
                    {...a11yProps(1)}
                  />
                </Tabs>
              </Box>
            </Grid>
            <Grid item xs={12} sm={12} md={12}>
              {productsTabsPlaceholder()}
            </Grid>
          </Grid>
        </Grid>
      )}
    </Grid>
  );
}
